/***************************************************************************************
 * FileName      : LoggingService.ts
 * Description   : 로깅 서비스 - 로깅 관리 (log4js 스타일)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2025.01.27
 * Modified Date : 2025.01.27 log4js 제거, 직접 로깅 구현으로 변경
 * Reference     : 
 ***************************************************************************************/

// 2025.01.27 : 박은정 : 로그 레벨 정의
enum LogLevel {
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

// 2025.01.27 : 박은정 : 콘솔에 로그 출력하는 함수
function logToConsole(level: LogLevel, message: string): void {
  const timestamp = new Date().toISOString();
  const logMessage = `[${timestamp}] [${level}] ${message}`;
  
  switch (level) {
    case LogLevel.INFO:
      console.info(logMessage);
      break;
    case LogLevel.WARN:
      console.warn(logMessage);
      break;
    case LogLevel.ERROR:
      console.error(logMessage);
      break;
  }
}

// 2025.01.27 : 박은정 : IP 주소를 가져오는 함수
async function getClientIP(): Promise<string> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || 'Unknown';
  } catch (error) {
    console.error('IP 주소를 가져오는 중 오류 발생:', error);
    return 'Unknown';
  }
}

// 2025.01.27 : 박은정 : 현재 일시를 포맷팅하는 함수
function getFormattedDateTime(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
}

// 2025.01.27 : 박은정 : 메인 화면 진입 로그를 기록하는 함수
export async function logMainScreenEntry(logContent?: string): Promise<void> {
  try {
    const dateTime = getFormattedDateTime();
    const ip = await getClientIP();
    
    const logMessage = {
      dateTime: dateTime,
      ip: ip,
      content: logContent || '메인 화면 진입',
      event: 'MAIN_SCREEN_ENTRY'
    };
    
    // 2025.01.27 : 박은정 : 콘솔에 로그 출력
    logToConsole(LogLevel.INFO, JSON.stringify(logMessage));
    
    // 2025.01.27 : 박은정 : 서버로 로그 전송하여 파일에 저장
    try {
      const response = await fetch('/api/log', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(logMessage)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('로그 서버 저장 성공:', result);
      } else {
        console.warn('로그 서버 저장 실패:', response.status, response.statusText);
      }
    } catch (fetchError) {
      // 서버 전송 실패 시에도 콘솔 로그는 유지
      console.warn('로그 서버 전송 실패 (콘솔 로그는 정상):', fetchError);
    }
  } catch (error) {
    console.error('로깅 중 오류 발생:', error);
  }
}

